import React from "react";
import T from "prop-types";
import { Map } from "immutable";
import { Col, Collapse } from "react-bootstrap";
import { Field, Fields, formValueSelector } from "redux-form";
import {
  disabledInputs,
  FormRow,
  CheckGroup,
  Check,
  TextArea,
  NumberInput,
  FormInput,
  CurrencyField
} from "MODULES/shared/components/Forms";
import withFormFields from "MODULES/shared/decorators/withFormFields";
import * as F from "MODULES/shared/components/Forms/FormTypes";
import {
  EXCHANGE_EVAL_IN_PROGRESS,
  EXCHANGE_SALE,
  EXCHANGE_SPECIAL_INSTRUCTIONS,
  FLAT_EXCHANGE,
  OUTRIGHT_VALUE,
  REPAIR_ADMIN_CHARGE_TYPE,
  REPAIR_ADMINISTRATION_CHARGE_DOLLARS,
  REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE,
  REPAIR_FEE,
  SHIPPED_AT
} from "MODULES/SalesOrders/constants";
import {
  EXPECTED_QUANTITY,
  QUANTITY,
  RECEIVED,
  SHIPPED
} from "APP_ROOT/constants";
import { INTERNAL_OUTRIGHT } from "MODULES/inventories/constants";
import { properCase } from "LIB/formatters/text";
import { EXCHANGE_FEE } from "MODULES/OutgoingQuotes/constants";
import { IconText } from "MODULES/shared/components";
import {
  CORE_DUE_DAYS,
  CORE_RETURN_DATE,
  CORE_EXPECTED_DATE
} from "MODULES/RepairOrders/constants";
import { connect } from "react-redux";
import { dateFormFormat } from "LIB/formatters/date";
import RepairAdminCharge from "MODULES/SalesOrders/components/Detail/Body/LineItems/Form/PartDetails/RepairAdminCharge";
import { BlanketExchangeAgreementWarning } from "../../../../ConvertFromCartForm/Warnings";

const disabledEnhancer = disabledInputs({
  Check,
  FormInput,
  CurrencyField,
  NumberInput
});

export function ExchangeSale({
  activeExchange,
  cancelled,
  canExchange,
  company,
  exchange_sale,
  flatExchange,
  inventory,
  lineItem,
  paid,
  commissionPaid,
  newRecord,
  shipped,
  initialDayValue,
  setFieldValue,
  canEditPartDetails = true,
  repairAdminChargeType,
  exchangeSaleValue
}) {
  const disabled = cancelled || paid;

  const inputs = disabledEnhancer(disabled || !canEditPartDetails);

  const currencyInputs = disabledEnhancer(
    disabled || !canEditPartDetails || commissionPaid
  );

  if (!newRecord && !activeExchange) return null;

  const qty =
    parseInt(inventory.get(QUANTITY), 10) +
    parseInt(inventory.get(EXPECTED_QUANTITY), 10);

  const shipDate = () => {
    if (lineItem.get(SHIPPED)) {
      return lineItem.get(SHIPPED_AT)
        ? new Date(lineItem.get(SHIPPED_AT))
        : new Date();
    }
    return new Date();
  };

  const calculateDueDays = returnDate => {
    const selectedDate = new Date(returnDate);

    const timeDiff = selectedDate.getTime() - shipDate().getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const dayValue = dayDiff + initialDayValue;

    return dayValue >= 0 ? dayValue : 0;
  };

  const handleCoreDueDaysChange = event => {
    if (!event.target) return null;
    if (Number(event.target.value) > 0) {
      const priorDate = shipDate();
      const futureDate = new Date(priorDate);
      futureDate.setTime(
        futureDate.getTime() + Number(event.target.value) * 86400000
      );
      setFieldValue(CORE_EXPECTED_DATE, dateFormFormat(futureDate));
    }

    return null;
  };

  const handleExpectedDateChange = event => {
    const dueDays = calculateDueDays(event.target.value);
    setFieldValue(CORE_DUE_DAYS, dueDays);
  };

  const exchange = exchangeSaleValue;

  return (
    <Collapse in={qty === 1} unmountOnExit>
      <div>
        <h5 className="h4">Exchange Sale</h5>
        <FormRow>
          {inventory.get(EXCHANGE_EVAL_IN_PROGRESS) ? (
            <Col md={5}>
              <CheckGroup
                title={[
                  properCase(INTERNAL_OUTRIGHT),
                  <IconText
                    iconSize="fa-stack-1x"
                    icon="fa-info-circle"
                    note="Create sales order (internal transaction only) to bill repair charges to customer and return core to stock unrepaired"
                  />
                ]}
                justified
              >
                <Field
                  name={INTERNAL_OUTRIGHT}
                  component={inputs.Check}
                  label="Yes"
                />
              </CheckGroup>
            </Col>
          ) : (
            <React.Fragment>
              <Col md={3}>
                <CheckGroup title={properCase(EXCHANGE_SALE)} justified>
                  <Field
                    name={EXCHANGE_SALE}
                    component={Check}
                    label={properCase(EXCHANGE_SALE)}
                    disabled={disabled || activeExchange || !canExchange}
                  />
                </CheckGroup>
              </Col>
              <Col md={3}>
                <CheckGroup title={properCase(FLAT_EXCHANGE)} justified>
                  <Field
                    name={FLAT_EXCHANGE}
                    component={Check}
                    label={properCase(FLAT_EXCHANGE)}
                    disabled={disabled || !canExchange || shipped}
                  />
                </CheckGroup>
              </Col>
              <Col md={7}>
                {newRecord && (
                  <BlanketExchangeAgreementWarning
                    company={company}
                    exchangeSale={exchange_sale.input.value}
                  />
                )}
              </Col>
            </React.Fragment>
          )}
        </FormRow>
        {exchange && (
          <React.Fragment>
            <FormRow>
              <Col md={6}>
                <Fields
                  names={[
                    REPAIR_ADMIN_CHARGE_TYPE,
                    REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE,
                    REPAIR_ADMINISTRATION_CHARGE_DOLLARS
                  ]}
                  component={RepairAdminCharge}
                />
              </Col>
            </FormRow>
            <FormRow>
              {repairAdminChargeType === "percentage" && (
                <Col md={3}>
                  <Field
                    name={REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE}
                    component={FormInput}
                    label="Repair Administration Charge (%)"
                    required
                  />
                </Col>
              )}
              {repairAdminChargeType === "dollars" && (
                <Col md={3}>
                  <Field
                    name={REPAIR_ADMINISTRATION_CHARGE_DOLLARS}
                    component={FormInput}
                    label="Repair Administration Charge ($)"
                    required
                  />
                </Col>
              )}
            </FormRow>
          </React.Fragment>
        )}
        <Collapse in={!!exchange_sale.input.value} unmountOnExit>
          <div>
            <FormRow>
              <Col md={3}>
                <currencyInputs.CurrencyField
                  required
                  name={EXCHANGE_FEE}
                  label={flatExchange ? "Flat Exchange Fee" : "Exchange Fee"}
                />
              </Col>
              <Col md={3}>
                <currencyInputs.CurrencyField
                  required
                  name={OUTRIGHT_VALUE}
                  label={properCase(OUTRIGHT_VALUE)}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col md={3}>
                <Field
                  name={CORE_EXPECTED_DATE}
                  component={FormInput}
                  type="date"
                  label={"Core Expected Date"}
                  onChange={handleExpectedDateChange}
                  disabled={!lineItem.get(RECEIVED)}
                />
              </Col>
              <Col md={3}>
                <Field
                  name={CORE_RETURN_DATE}
                  component={FormInput}
                  type="date"
                  label={properCase(CORE_RETURN_DATE)}
                  disabled
                />
              </Col>
              <Col md={3}>
                <Field
                  name={CORE_DUE_DAYS}
                  label={properCase(CORE_DUE_DAYS)}
                  component={NumberInput}
                  roundDigits={0}
                  onChange={handleCoreDueDaysChange}
                  disabled={lineItem.get(RECEIVED)}
                />
              </Col>
              <Col md={3}>
                <currencyInputs.CurrencyField
                  name={REPAIR_FEE}
                  label="Avg Repair (NTX)"
                  required={flatExchange}
                />
              </Col>
            </FormRow>
            <FormRow className="row-col-gutters-half">
              <Col md={12}>
                <Field
                  name={EXCHANGE_SPECIAL_INSTRUCTIONS}
                  component={TextArea}
                  label="Special Instructions (to be incl. on Exch Agreement)"
                  maxLength={150}
                  rows="5"
                  cols="50"
                  disabled={!canEditPartDetails}
                />
              </Col>
            </FormRow>
            <hr className="hr-sm" />
          </div>
        </Collapse>
      </div>
    </Collapse>
  );
}

ExchangeSale.propTypes = {
  activeExchange: T.bool,
  cancelled: T.bool,
  canExchange: T.bool,
  company: T.shape({}),
  exchanged: T.bool,
  flatExchange: T.bool,
  paid: T.bool,
  shipped: T.bool,
  commissionPaid: T.bool,
  inventory: T.instanceOf(Map),
  exchange_sale: F.formField(F.boolInput).isRequired,
  newRecord: T.bool,
  canEditPartDetails: T.bool,
  setFieldValue: T.func.isRequired,
  repairAdminChargeType: T.string,
  exchangeSaleValue: T.bool
};

const selector = formValueSelector("salesOrderLineItem");

const mapStateToProps = state => ({
  repairAdminChargeType: selector(state, "repair_admin_charge_type"),
  exchangeSaleValue: selector(state, EXCHANGE_SALE)
});

export default connect(mapStateToProps)(
  withFormFields(ExchangeSale, [EXCHANGE_SALE])
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/Form/ExchangeSale.jsx